/**
 * useFavorites — montures sauvegardées par le client connecté.
 * Table `favorite_frames` (RLS scopée sur auth.uid()).
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthSession } from "@/hooks/useAuthSession";

export type FavoriteFrame = {
  id: string;
  frame_id: string;
  created_at: string;
  frame: {
    id: string;
    slug: string;
    name: string;
    name_en: string | null;
    color: string | null;
    color_en: string | null;
    base_image_url: string | null;
  } | null;
};

export function useFavorites() {
  const { user } = useAuthSession();
  const userId = user?.id ?? null;

  return useQuery<FavoriteFrame[]>({
    queryKey: ["favorites", userId],
    enabled: !!userId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("favorite_frames")
        .select(
          "id, frame_id, created_at, frame:frames(id, slug, name, name_en, color, color_en, base_image_url)",
        )
        .order("created_at", { ascending: false })
        .limit(60);
      if (error) throw error;
      return (data ?? []) as unknown as FavoriteFrame[];
    },
  });
}

export function useToggleFavorite() {
  const qc = useQueryClient();
  const { user } = useAuthSession();

  return useMutation({
    mutationFn: async ({ frameId, isFavorite }: { frameId: string; isFavorite: boolean }) => {
      if (!user) throw new Error("not_authenticated");
      if (isFavorite) {
        const { error } = await supabase
          .from("favorite_frames")
          .delete()
          .eq("user_id", user.id)
          .eq("frame_id", frameId);
        if (error) throw error;
        return false;
      }
      const { error } = await supabase
        .from("favorite_frames")
        .insert({ user_id: user.id, frame_id: frameId });
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["favorites", user?.id ?? null] });
    },
  });
}
